import { useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, QrCode, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { DestinationPicker } from './DestinationPicker'
import { ExcludeMoveButton } from './ExcludeMoveButton'
import {
  claimedSlots,
  groupMovesByContainer,
  itemCode,
  itemDetail,
  itemKindLabel,
  moveName,
  unplacedReason,
  type ReviewableMove,
} from './movePreview'
import type { MoveItem } from '@/types/storage'

/**
 * What the row is carrying, with its kind as a badge so a product (a patient's
 * bag) and a lot (a reagent) cannot be mistaken for each other in a long list.
 */
export function MoveItemCell({ item }: { item: MoveItem | null }) {
  const kind = itemKindLabel(item)
  const detail = itemDetail(item)
  return (
    <div className='min-w-0'>
      <div className='flex items-center gap-1.5'>
        {kind && (
          <span
            className={cn(
              'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              item?.kind === 'PRODUCT' ? 'bg-violet-100 text-violet-800' : 'bg-sky-100 text-sky-800',
            )}
          >
            {kind}
          </span>
        )}
        {/* Never truncated: the code is what gets matched against the label. */}
        <span
          className={cn(
            'whitespace-nowrap',
            item ? 'font-mono text-[13px] font-medium' : 'italic text-muted-foreground',
          )}
        >
          {itemCode(item)}
        </span>
      </div>
      {detail && <p className='truncate text-xs text-muted-foreground'>{detail}</p>}
    </div>
  )
}

/**
 * The plan, one box at a time.
 *
 * Grouped rather than a flat list because a thousand slots is unreviewable and
 * because a person checks a freezer box by box — which is also the order they
 * will work it. Groups start collapsed above a handful: opening a 10,000-slot
 * plan should not lay out 10,000 rows, the same reason the storage browser
 * expands by row budget.
 */

/** A row the caller can act on. Absent handlers make the table read-only. */
export type MoveActions = {
  onRetarget?: (move: ReviewableMove, targetNodeRef: string, targetNodePath: string) => void
  onExclude?: (move: ReviewableMove, skipReason: string) => void
  /** Bring back a row excluded on the preview (nothing is stored yet). */
  onRestore?: (move: ReviewableMove) => void
  /** The replacement unit, where the location picker starts looking. */
  targetUnitId?: string
  /** Present only once the plan exists — a preview has nothing to undo to. */
  busyOrdinal?: number | null
}

const OPEN_BUDGET = 3

export function MovePreviewTable({
  moves,
  actions,
  onShowQr,
}: {
  moves: ReviewableMove[]
  actions?: MoveActions
  /**
   * Offers each row's QR code for a handset. Absent on a preview or a draft:
   * only an approved plan's moves are worth walking to a freezer with.
   */
  onShowQr?: (move: ReviewableMove) => void
}) {
  const groups = useMemo(() => groupMovesByContainer(moves), [moves])
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(groups.slice(0, OPEN_BUDGET).map((g) => g.container)),
  )

  const toggle = (container: string) => {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(container)) next.delete(container)
      else next.add(container)
      return next
    })
  }

  if (moves.length === 0) {
    return (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        This unit is empty — there is nothing to transfer.
      </p>
    )
  }

  return (
    <div className='space-y-2'>
      {groups.map((group) => {
        const expanded = open.has(group.container)
        const problems = group.moves.filter(
          (m) => m.status !== 'SKIPPED' && (!m.targetNodeRef || m.conditionVerdict !== 'ok'),
        ).length

        return (
          <div key={group.container} className='rounded-md border'>
            <button
              type='button'
              onClick={() => toggle(group.container)}
              className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50'
            >
              {expanded ? (
                <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
              ) : (
                <ChevronRight className='h-4 w-4 shrink-0 text-muted-foreground' />
              )}
              <span className='font-medium'>{group.container}</span>
              <span className='text-muted-foreground'>
                {group.moves.length} occupant{group.moves.length === 1 ? '' : 's'}
              </span>
              {problems > 0 && (
                <span className='ml-auto flex items-center gap-1 text-amber-700'>
                  <AlertTriangle className='h-3.5 w-3.5' />
                  {problems} to settle
                </span>
              )}
            </button>

            {expanded && (
              <div className='overflow-x-persistent border-t'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground'>
                      <th className='px-3 py-2 font-medium'>Item</th>
                      <th className='px-3 py-2 font-medium'>From</th>
                      <th className='px-3 py-2 font-medium'>To</th>
                      <th className='px-3 py-2 font-medium'>Status</th>
                      {actions && (
                        // Pinned to the right edge: with long From / To paths the table
                        // scrolls sideways, and the actions were scrolled out of view
                        // (z8nrz7f2gh).
                        <th className='sticky right-0 bg-muted shadow-[-8px_0_8px_-8px_rgb(0_0_0/0.15)] px-3 py-2 text-right font-medium'>Actions</th>
                      )}
                      {onShowQr && <th className='px-3 py-2 font-medium' />}
                    </tr>
                  </thead>
                  <tbody>
                    {group.moves.map((m) => {
                      const excluded = m.status === 'SKIPPED'
                      const unmapped = !m.targetNodeRef
                      const unsuitable = m.conditionVerdict !== 'ok'
                      const busy = actions?.busyOrdinal === m.ordinal

                      return (
                        <tr key={m.ordinal} className='border-b last:border-0'>
                          <td
                            className={cn(
                              'max-w-[22rem] px-3 py-2',
                              excluded && 'text-muted-foreground line-through',
                            )}
                          >
                            <MoveItemCell item={m.item} />
                          </td>
                          <td
                            className={cn(
                              'whitespace-nowrap px-3 py-2 font-mono text-[13px]',
                              excluded && 'text-muted-foreground line-through',
                            )}
                          >
                            {m.sourceNodePath}
                          </td>
                          <td className='px-3 py-2 font-mono text-[13px]'>
                            {m.targetNodePath ? (
                              <span className='break-all'>{m.targetNodePath}</span>
                            ) : (
                              // z8nrz7f2gh: say WHY there is no slot, not "nowhere yet".
                              <span className='block max-w-[16rem] whitespace-normal font-sans text-[12px] italic text-amber-700'>
                                {unplacedReason(m) ?? 'Needs a destination'}
                              </span>
                            )}
                          </td>
                          <td className='px-3 py-2'>
                            {m.status === 'MOVED' ? (
                              <span className='text-emerald-700'>Moved</span>
                            ) : excluded && m.scannedAt ? (
                              // Left behind at the freezer (z8nrz7e7ak), as
                              // opposed to excluded while the plan was a draft.
                              <span className='text-amber-700'>
                                Skipped{m.skipReason ? `: ${m.skipReason}` : ''}
                              </span>
                            ) : excluded ? (
                              <span className='text-muted-foreground'>Excluded</span>
                            ) : unmapped ? (
                              <span className='text-amber-700'>No destination</span>
                            ) : unsuitable ? (
                              // The destination is kept on purpose: a person
                              // retargeting needs to see WHICH slot is wrong.
                              <span className='text-amber-700'>{m.conditionVerdict}</span>
                            ) : (
                              <span className='text-emerald-700'>Planned</span>
                            )}
                          </td>
                          {actions && (
                            <td className='sticky right-0 bg-background shadow-[-8px_0_8px_-8px_rgb(0_0_0/0.15)] whitespace-nowrap px-3 py-2 text-right'>
                              {excluded && !m.scannedAt && actions.onRestore && (
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  className='h-7 text-muted-foreground'
                                  onClick={() => actions.onRestore?.(m)}
                                >
                                  <Undo2 className='mr-1 h-3.5 w-3.5' />
                                  Include
                                </Button>
                              )}
                              {!excluded && (
                                <div className='inline-flex items-center gap-1'>
                                  {actions.onRetarget && actions.targetUnitId && (
                                    <DestinationPicker
                                      lotId={m.lotId ?? null}
                                      productId={m.productId ?? null}
                                      defaultUnitId={actions.targetUnitId}
                                      claimed={claimedSlots(moves, m.ordinal)}
                                      current={m.targetNodeRef}
                                      disabled={busy}
                                      onPick={(ref, path) => actions.onRetarget?.(m, ref, path)}
                                    />
                                  )}
                                  {actions.onExclude && (
                                    // A reason is mandatory server-side, so it
                                    // is asked for before the request.
                                    <ExcludeMoveButton
                                      name={moveName(m)}
                                      disabled={busy}
                                      onExclude={(reason) => actions.onExclude?.(m, reason)}
                                    />
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                          {onShowQr && (
                            <td className='px-3 py-2 text-right'>
                              {!excluded && (
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  className='h-7'
                                  aria-label={`QR code for ${moveName(m)}`}
                                  title='QR code for a handset'
                                  onClick={() => onShowQr(m)}
                                >
                                  <QrCode className='h-3.5 w-3.5' />
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
